import { Close, WarningAmber } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { makeStyles } from "components/mui-core";
import { FloatingNotification } from "components/FloatingNotification/FloatingNotification";
import localStorageProvider from "dataProvidersDeprecated/localStorageProvider";
import { addHours, isAfter } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useAppNotifications } from "./AppNotificationProvider";

const useStyles = makeStyles()({
  root: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  },
  text: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    flexWrap: "nowrap",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  close: {
    cursor: "pointer",
  },
});

export const NOTIFICATIONS_SUMMARY_DISMISS_TIME = 4;

export const AppNotificationSummary = ({ onClick }) => {
  const sidebarWidth =
    document.querySelector("#sidebar > div")?.clientWidth || 0;
  const { classes: styles } = useStyles();
  const { visibleNotifications } = useAppNotifications();
  const [forceHide, setForceHide] = useState(false);

  // Even though this could be moved to AppNotificationProvider, if we do so
  // we lose the "hacky" way to react on localStorage. For this I use "forceHide"
  // to force component re-render
  const summaryNotifications = useMemo(() => {
    const current =
      localStorageProvider.getDismissedRiskNotificationSummary() || {};

    const newNotifications = visibleNotifications.reduce(
      (acum: any, curr: any) => {
        // To determine we have a new notification we need to:
        // 1. If a new notification id exists, its automatically new
        // 2. Is a notification id exists in currentlyVisible and in current, then:
        //    If the current one timestamp is older than 4h, then it's new again

        // Case 1
        if (!current[curr.id]) {
          return [...acum, curr];
        }

        // Case 2
        if (isAfter(new Date(), current[curr.id])) {
          return [...acum, curr];
        }

        return acum;
      },
      []
    );

    return newNotifications;
  }, [visibleNotifications, forceHide]);

  const onClose = () => {
    localStorageProvider.setDismissedRiskNotificationSummary({
      ...localStorageProvider.getDismissedRiskNotificationSummary(),
      ...visibleNotifications.reduce((acum, curr) => {
        return {
          ...acum,
          [curr.id]: addHours(
            new Date(),
            NOTIFICATIONS_SUMMARY_DISMISS_TIME
          ).getTime(),
        };
      }, {}),
    });

    setForceHide(true);
  };

  const handleClick = () => {
    if (onClick) {
      onClick?.();
      onClose();
    }
  };

  useEffect(() => {
    // Since we can't react on localStorage changes, this simulates the reappearance
    if (forceHide) {
      const t = setTimeout(() => setForceHide(false));

      return () => clearTimeout(t);
    }
  }, [forceHide]);

  // To clarify:
  // We show only if we have new notifications (summaryNotifications)
  // But we show the amount of total visibleNotifications we have

  return summaryNotifications.length && !forceHide ? (
    <FloatingNotification leftOffset={sidebarWidth}>
      <div className={styles.root}>
        <span
          className={styles.text}
          onClick={handleClick}>
          <WarningAmber color="warning" /> {visibleNotifications.length} alerts
          for this organization{" "}
        </span>

        <div className={styles.actions}>
          <Button
            variant="text"
            color="warning"
            sx={{ border: "none" }}
            onClick={handleClick}>
            Open alerts
          </Button>

          <Close
            className={styles.close}
            fontSize={"smaller" as any}
            onClick={onClose}
          />
        </div>
      </div>
    </FloatingNotification>
  ) : null;
};
